// Land → Cross Kart (2 Seater) feature-tree windows (the `land/cross-kart`
// surface — featureWindowSurface.ts). Mirrors spaceframeWindows.ts's
// lazy-loading pattern exactly, but every entry is handed the Cross Kart
// store (crossKartStore), not the FIA workbench's singleton — reused
// spaceframe window components already accept an optional `store` prop for
// exactly this (storeApi.ts's documented contract).
//
// Tree order (PLAN.md): Rulebook -> Vehicle & Occupants -> Dimensions &
// Stretch -> 3D Sketch -> Tube Spec -> Front Suspension -> Rear Suspension ->
// Powertrain -> Wheels & Tyres -> Weldment -> Bends & Fabrication ->
// Compliance -> Structural (FEA) -> Kinematics -> Build Dossier (PDF). The
// "Rulebook PDF link" item in PLAN.md's prose is folded into the Rulebook
// window itself (it already embeds the doc viewer + rule->page jump links,
// same as FiaRegulationsWindow) rather than duplicating that UI as a second
// tree row.

import { createElement, lazy, Suspense, type ComponentType } from 'react';
import type { FeatureWindowItem } from '@/components/featureTreeSidebar/featureWindowRegistry';
import type { SpaceframeStore } from '@/components/spaceframe/storeTypes';

// NOT a top-level `import { crossKartStore } from './store'` — this file is
// imported eagerly by featureWindowRegistry.ts (always-loaded, like
// spaceframeWindows.ts), and `./store` creates the workbench singleton
// (pulling in docDefaults/catalog) as a module-level side effect. Deferring
// it into each lazy loader keeps that cost out of the first-paint bundle for
// every user who never opens Land → Cross Kart (Lazy3D boundary).
type WindowProps = { enabled: boolean; store?: SpaceframeStore };
type Mod = Record<string, ComponentType<WindowProps>>;

// One dynamic import per window file (each window is its own lazy chunk —
// Lazy3D boundary). Reused spaceframe windows load from their existing chunk;
// the crosskart-only windows get their own.
const LOADERS: Record<string, () => Promise<unknown>> = {
  RulebookWindow: () => import('./windows/RulebookWindow'),
  OccupantWindow: () =>
    import('@/components/spaceframe/windows/OccupantWindow'),
  DimensionsStretchWindow: () => import('./windows/DimensionsStretchWindow'),
  SketchWindow: () => import('@/components/spaceframe/windows/SketchWindow'),
  TubeSpecWindow: () =>
    import('@/components/spaceframe/windows/TubeSpecWindow'),
  FrontSuspensionWindow: () => import('./windows/FrontSuspensionWindow'),
  RearSuspensionWindow: () => import('./windows/RearSuspensionWindow'),
  PowertrainWindow: () => import('./windows/PowertrainWindow'),
  WheelsTyresWindow: () => import('./windows/WheelsTyresWindow'),
  WeldmentWindow: () =>
    import('@/components/spaceframe/windows/WeldmentWindow'),
  BendsWindow: () => import('@/components/spaceframe/windows/BendsWindow'),
  ComplianceWindow: () =>
    import('@/components/spaceframe/windows/ComplianceWindow'),
  FeaWindow: () => import('@/components/spaceframe/windows/FeaWindow'),
  KinematicsWindow: () => import('./windows/KinematicsWindow'),
  ComplianceReportWindow: () =>
    import('@/components/spaceframe/windows/ComplianceReportWindow'),
};

/** Loads the window component AND the store module together (both already
 *  behind this same lazy chunk boundary) so `crossKartStore` is created only
 *  once a Cross Kart window actually renders — never at import time. */
const load = (name: string): ComponentType<{ enabled: boolean }> =>
  lazy(async () => {
    const [mod, { crossKartStore }] = await Promise.all([
      LOADERS[name](),
      import('./store'),
    ]);
    const Comp = (mod as Mod)[name];
    return {
      default: (props: { enabled: boolean }) =>
        createElement(Comp, { ...props, store: crossKartStore }),
    };
  });

const ROWS: { key: string; label: string; width: number; name: string }[] = [
  { key: 'ck-rulebook', label: 'Rulebook', width: 420, name: 'RulebookWindow' },
  {
    key: 'ck-occupant',
    label: 'Vehicle & Occupants',
    width: 380,
    name: 'OccupantWindow',
  },
  {
    key: 'ck-dimensions',
    label: 'Dimensions & Stretch',
    width: 380,
    name: 'DimensionsStretchWindow',
  },
  { key: 'ck-sketch', label: '3D Sketch', width: 380, name: 'SketchWindow' },
  {
    key: 'ck-tube-spec',
    label: 'Tube Spec',
    width: 400,
    name: 'TubeSpecWindow',
  },
  {
    key: 'ck-front-susp',
    label: 'Front Suspension',
    width: 400,
    name: 'FrontSuspensionWindow',
  },
  {
    key: 'ck-rear-susp',
    label: 'Rear Suspension',
    width: 400,
    name: 'RearSuspensionWindow',
  },
  {
    key: 'ck-powertrain',
    label: 'Powertrain',
    width: 380,
    name: 'PowertrainWindow',
  },
  {
    key: 'ck-wheels',
    label: 'Wheels & Tyres',
    width: 380,
    name: 'WheelsTyresWindow',
  },
  { key: 'ck-weldment', label: 'Weldment', width: 360, name: 'WeldmentWindow' },
  {
    key: 'ck-bends',
    label: 'Bends & Fabrication',
    width: 420,
    name: 'BendsWindow',
  },
  {
    key: 'ck-compliance',
    label: 'Compliance',
    width: 380,
    name: 'ComplianceWindow',
  },
  { key: 'ck-fea', label: 'Structural (FEA)', width: 380, name: 'FeaWindow' },
  {
    key: 'ck-kinematics',
    label: 'Kinematics',
    width: 380,
    name: 'KinematicsWindow',
  },
  {
    key: 'ck-dossier',
    label: 'Build Dossier (PDF)',
    width: 520,
    name: 'ComplianceReportWindow',
  },
];

/** Flag-off legacy pane (feature-tree windows kill-switch OFF): the same
 *  windows stacked inline in the sidebar, in tree order — mirrors
 *  SpaceframeInlinePane. */
export function CrossKartInlinePane({ enabled }: { enabled: boolean }) {
  return createElement(
    'div',
    { className: 'space-y-4' },
    CROSSKART_FEATURE_WINDOWS.map((w) =>
      createElement(
        'section',
        { key: w.key },
        createElement(
          'h3',
          {
            className: 'mb-2 text-xs font-semibold text-telekinesis-neutral-50',
          },
          w.label,
        ),
        w.render(enabled),
      ),
    ),
  );
}

export const CROSSKART_FEATURE_WINDOWS: FeatureWindowItem[] = ROWS.map(
  ({ key, label, width, name }) => {
    const Comp = load(name);
    return {
      key,
      label,
      width,
      render: (enabled: boolean) =>
        createElement(
          Suspense,
          { fallback: null },
          createElement(Comp, { enabled }),
        ),
    };
  },
);
